import React from "react";
import { User, Home, Building2, TrendingUp, Sparkles } from "lucide-react";
import type { LifeGlance } from "@/lib/infiniteSelves";
export interface LifeGlanceCardProps {
  glance: LifeGlance;
  cardHue?: number;
  className?: string;
  titlePrefix?: string;
}
export default function LifeGlanceCard({
  glance,
  cardHue = 186,
  className = "",
  titlePrefix
}: LifeGlanceCardProps): JSX.Element {
  if (!glance) return <></>;
  return <div className={`p-4 sm:p-5 rounded-xl bg-card/85 border border-primary/30 shadow-md space-y-3.5 relative overflow-hidden ${className}`} role="region" aria-label="Life at a Glance">
      <div className="absolute top-0 left-0 right-0 h-0.5" style={{
      background: `linear-gradient(90deg, transparent, hsl(${cardHue} 100% 55%), transparent)`
    }} aria-hidden="true" />

      <div className="flex items-center justify-between gap-2 border-b border-border/60 pb-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <Sparkles className="size-3.5 text-primary shrink-0" aria-hidden="true" />
          <span className="text-xs font-mono uppercase tracking-widest text-primary font-bold truncate">
            {titlePrefix ? `${titlePrefix} · Life at a Glance` : "Life at a Glance"}
          </span>
        </div>
        <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground shrink-0 border border-border/60 bg-muted/40 px-2 py-0.5 rounded-full">
          Free Preview
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
        {/* Age */}
        <div className="flex items-start gap-2.5 p-2.5 rounded-lg bg-muted/40 border border-border/60">
          <User className="size-4 text-primary shrink-0 mt-0.5" aria-hidden="true" />
          <div className="min-w-0">
            <span className="text-[10px] font-mono uppercase text-muted-foreground block font-semibold">
              Age
            </span>
            <span className="font-serif font-bold text-foreground text-sm">
              {glance.age} Earth-equivalent years
            </span>
          </div>
        </div>

        {/* Standing */}
        <div className="flex items-start gap-2.5 p-2.5 rounded-lg bg-muted/40 border border-border/60">
          <TrendingUp className="size-4 text-chart-3 shrink-0 mt-0.5" aria-hidden="true" />
          <div className="min-w-0">
            <span className="text-[10px] font-mono uppercase text-muted-foreground block font-semibold">
              Standing & Class
            </span>
            <span className="font-sans text-foreground/90 font-medium truncate block">
              {glance.standing || "Established standing"}
            </span>
          </div>
        </div>

        {/* Home */}
        <div className="flex items-start gap-2.5 p-2.5 rounded-lg bg-muted/40 border border-border/60">
          <Home className="size-4 text-primary shrink-0 mt-0.5" aria-hidden="true" />
          <div className="min-w-0">
            <span className="text-[10px] font-mono uppercase text-muted-foreground block font-semibold">
              Dwelling & Habitat
            </span>
            <span className="font-sans text-foreground/90 font-medium line-clamp-2">
              {glance.home || "Domestic residence"}
            </span>
          </div>
        </div>

        {/* Society */}
        <div className="flex items-start gap-2.5 p-2.5 rounded-lg bg-muted/40 border border-border/60">
          <Building2 className="size-4 text-chart-4 shrink-0 mt-0.5" aria-hidden="true" />
          <div className="min-w-0">
            <span className="text-[10px] font-mono uppercase text-muted-foreground block font-semibold">
              Social Order
            </span>
            <span className="font-sans text-foreground/90 font-medium line-clamp-2">
              {glance.society || "Organized civic structure"}
            </span>
          </div>
        </div>
      </div>
    </div>;
}